import { CmsValue } from 'core/type/BootstrapConfig.types';
import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { selectCmsFromBootstrap, selectExperimentsFromBootstrap, selectFeatureBehaviorFromBootstrap, selectRtlFromBootstrap } from 'store/slice/bootstrap';
import { selectActivityContentLocale, selectInclusionExclusionFromDetail, selectPossibleOfferOptions } from 'component/page/detail/store/availabilitySelectorIndex';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import {
    selectActivityOfferDetails,
    selectActivityTokenFromDetailState,
    selectExtraFeeContentFromExtraFeeState,
    selectExtraFeeFromContentDetailGenericSectionState,
} from 'component/page/detail/store/detail.selectors';
import { OfferDetail } from 'gql/autogen/OfferDetail';
import { BenefitItemsReference } from 'gql/autogen/BenefitItemsReference';
import { GenericContentSection } from 'gql/autogen/GenericContentSection';
import { GenericContent } from 'gql/autogen/GenericContent';
import { SelectedOfferState } from 'component/page/detail/store/availabilityIndex.types';
import { fetchAvailability, setBookingRequirement, setOffer } from 'component/page/detail/store/availabilityIndex';
import { Offer } from 'gql/autogen/Offer';
import { scrollToTargetAdjusted } from 'core-framework/util/Dom';
import { CARD_EXPAND_TIME_OFFSET, TAB_HEADER_OFFSET } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { OfferGroupDetail } from 'gql/autogen/OfferGroupDetail';
import { ActivityOfferToken } from 'core/type/Details.types';
import { Redemption } from 'gql/autogen/Redemption';
import { selectRedemptionFromDetail } from 'component/page/detail/store/availability.selectors';

interface StateProps {
    isRTL: boolean;
    cms: CmsValue;
    contentLocale: string;
    possibleOfferOptions: EnrichedOfferOption[];
    offerDetails: OfferDetail[];
    activityTokenFromState: string;
    inclusions?: BenefitItemsReference[];
    exclusions?: BenefitItemsReference[];
    extraFeeSection?: GenericContentSection;
    extraFeeContent?: GenericContent[];
    isOfferComparison: boolean;
    isHideSeeDetailsButtonForNoContent: boolean;
    redemption?: Redemption;
}

interface DispatchProps {
    setOffer: (offer: SelectedOfferState) => void;
    setBookingRequirementById: (bookingRequirementId: number) => void;
    onSelectOffer: (offerToken: ActivityOfferToken, navigationHash: string) => void;
}

interface OwnProps {
    index: number;
    isSelectedOffer: boolean;
    setIsDisplayWarning: (isDisplayWarning: boolean) => void;
    isDisplayWarning: boolean;
    offerIndex: number;
    offer: Offer;
    navigationId: string;
    isLoading: boolean;
    offerGroupDetail: OfferGroupDetail | null;
}

export type Props = StateProps & DispatchProps & OwnProps;

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setOffer: (offer: SelectedOfferState) => {
        dispatch(setOffer(offer));
    },
    setBookingRequirementById: (bookingRequirementId: number) => {
        dispatch(setBookingRequirement({ bookingRequirementId, isResetSearchCriteriaStatus: true }));
    },
    onSelectOffer: (offerToken: ActivityOfferToken, navigationHash: string) => {
        fetchAvailability(offerToken.activityToken, { excludePassengers: false, enableOfferFilter: offerToken.enableOfferFilter });
        setTimeout(() => {
            scrollToTargetAdjusted(navigationHash, TAB_HEADER_OFFSET);
        }, CARD_EXPAND_TIME_OFFSET);
    },
});

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectExperimentsFromBootstrap,
    selectActivityContentLocale,
    selectRtlFromBootstrap,
    selectPossibleOfferOptions,
    selectActivityOfferDetails,
    selectInclusionExclusionFromDetail,
    selectExtraFeeFromContentDetailGenericSectionState,
    selectExtraFeeContentFromExtraFeeState,
    selectActivityTokenFromDetailState,
    selectFeatureBehaviorFromBootstrap,
    selectRedemptionFromDetail,
    (
        cms,
        experiments,
        contentLocale,
        isRTL,
        possibleOfferOptions,
        offerDetails,
        { inclusions, exclusions },
        extraFeeSection,
        extraFeeContent,
        activityTokenFromState,
        featureBehaviours,
        redemption,
    ): StateProps => {
        const isOfferComparison = featureBehaviours.OfferComparison;
        const isHideSeeDetailsButtonForNoContent = featureBehaviours.HideSeeDetailsButtonForNoContent;
        return {
            cms,
            contentLocale,
            isRTL,
            possibleOfferOptions,
            offerDetails,
            inclusions,
            exclusions,
            extraFeeSection,
            extraFeeContent,
            activityTokenFromState,
            isOfferComparison,
            isHideSeeDetailsButtonForNoContent,
            redemption,
        };
    },
);

const getModalContent = (offerDetails: OfferDetail[], offerDetailsReference: string) => {
    return offerDetails.find((offerDetail) => offerDetail.offerDetailRef === offerDetailsReference);
};

export const mapBenefitItems = (props: Props) => {
    const { offerDetails, inclusions, exclusions, extraFeeSection, extraFeeContent, offer, redemption } = props;
    const offerDetailsReference = offer.enrichedOfferOption?.[0].offerDetailsReference || '';
    const modalContent = getModalContent(offerDetails, offerDetailsReference);

    const validInclusions = inclusions && inclusions.find((inclusion) => inclusion.id === modalContent?.inclusionRefId);
    const validExclusions = exclusions && exclusions.find((exclusion) => exclusion.id === modalContent?.exclusionRefId);
    const hasInclusions = (validInclusions?.benefitItems?.length || 0) > 0;
    const hasExclusions = (validExclusions?.benefitItems?.length || 0) > 0;
    const hasExtraFee = extraFeeSection && extraFeeContent && extraFeeContent.length > 0;

    return {
        hasInclusions,
        hasExclusions,
        hasExtraFee,
        modalContent,
        inclusionsBenefits: validInclusions?.benefitItems,
        exclusionBenefits: validExclusions?.benefitItems,
        redemption,
    };
};
